/**
 * FolderService
 * Service for folder management operations
 * Route: api/folder
 */
import { BaseService } from '../base/BaseService';
/**
 * FolderService
 * Handles folder creation, organization, and management operations
 */
export class FolderService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? FolderService.DEFAULT_SERVICE_PATH,
            basePath: FolderService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== FOLDER OPERATIONS ====================
    /**
     * Create a new folder
     * POST api/folder/create
     */
    async create(data) {
        const response = await this.createRaw(data);
        if (!response.data) {
            throw new Error(response.message || 'Failed to create folder');
        }
        return response.data;
    }
    /**
     * Create folder with full response
     */
    createRaw(data) {
        const endpoint = this.getEndpoint('create', 'create');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, data);
    }
    /**
     * List folders with filters
     * GET api/folder/list
     */
    async list(params) {
        const response = await this.listRaw(params);
        return response.data ?? [];
    }
    /**
     * List folders with full response
     */
    listRaw(params) {
        const endpoint = this.getEndpoint('list', 'list');
        const url = this.buildUrlWithParams(endpoint, params);
        return this.http.get(url);
    }
    /**
     * Get folder by ID
     * GET api/folder/{id}
     */
    async getById(id) {
        const response = await this.getByIdRaw(id);
        if (!response.data) {
            throw new Error(response.message || 'Folder not found');
        }
        return response.data;
    }
    /**
     * Get folder by ID with full response
     */
    getByIdRaw(id) {
        const endpoint = this.getEndpoint('getById', '{id}');
        const url = this.buildUrl(endpoint.replace('{id}', id));
        return this.http.get(url);
    }
    /**
     * Rename a folder
     * PUT api/folder/{id}/rename
     */
    async rename(id, data) {
        const response = await this.renameRaw(id, data);
        if (!response.data) {
            throw new Error(response.message || 'Failed to rename folder');
        }
        return response.data;
    }
    /**
     * Rename folder with full response
     */
    renameRaw(id, data) {
        const endpoint = this.getEndpoint('rename', '{id}/rename');
        const url = this.buildUrl(endpoint.replace('{id}', id));
        return this.http.put(url, data);
    }
    /**
     * Move a folder to a new parent
     * POST api/folder/{id}/move
     */
    async move(id, data) {
        const response = await this.moveRaw(id, data);
        if (!response.data) {
            throw new Error(response.message || 'Failed to move folder');
        }
        return response.data;
    }
    /**
     * Move folder with full response
     */
    moveRaw(id, data) {
        const endpoint = this.getEndpoint('move', '{id}/move');
        const url = this.buildUrl(endpoint.replace('{id}', id));
        return this.http.post(url, data);
    }
    /**
     * Delete a folder
     * DELETE api/folder/{id}
     */
    async delete(id, deleteContents = false) {
        const response = await this.deleteRaw(id, deleteContents);
        return response.success;
    }
    /**
     * Delete folder with full response
     */
    deleteRaw(id, deleteContents = false) {
        const endpoint = this.getEndpoint('delete', '{id}');
        const url = this.buildUrlWithParams(endpoint.replace('{id}', id), { deleteContents });
        return this.http.delete(url);
    }
    /**
     * Get folder tree structure
     * GET api/folder/tree
     */
    async getTree(params) {
        const response = await this.getTreeRaw(params);
        return response.data ?? [];
    }
    /**
     * Get folder tree with full response
     */
    getTreeRaw(params) {
        const endpoint = this.getEndpoint('tree', 'tree');
        const url = this.buildUrlWithParams(endpoint, params);
        return this.http.get(url);
    }
}
FolderService.DEFAULT_SERVICE_PATH = 'files';
FolderService.BASE_PATH = 'api/folder';
//# sourceMappingURL=FolderService.js.map